// File Location: src/components/common/PrivateRoute.jsx

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDashboardRouteByRole } from '../../utils/navigationUtils';
import { Loader } from 'lucide-react';

const PrivateRoute = ({ children, requiredRole, requiredRoles }) => {
  const { currentUser, userRole, activeRole, loading } = useAuth();
  const location = useLocation();

  // Handle browser navigation to prevent going back to public routes
  useEffect(() => {
  if (!loading && currentUser && (activeRole || userRole)) {
      const handlePopState = (event) => {
        const currentPath = window.location.pathname;
        // If user tries to navigate back to login or home, redirect to dashboard
        if (currentPath === '/' || currentPath === '/login') {
          event.preventDefault();
          const dashboardRoute = getDashboardRouteByRole(activeRole || userRole);
          window.history.pushState(null, '', dashboardRoute);
        }
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [currentUser, userRole, activeRole, loading]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-teal-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated - store the intended location
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Simple role check - if wrong role, redirect to their correct dashboard
  const effectiveRole = activeRole || userRole;

  if (requiredRole && effectiveRole !== requiredRole) {
    const userDashboard = getDashboardRouteByRole(effectiveRole);
    return <Navigate to={userDashboard} replace />;
  }

  if (requiredRoles && !requiredRoles.includes(effectiveRole)) {
    const userDashboard = getDashboardRouteByRole(effectiveRole);
    return <Navigate to={userDashboard} replace />;
  }

  return children;
};

export default PrivateRoute;